import { DomainError } from "./errors";
import { MAX_STOCK_QTY_Q4 } from "./stock";
import type { BaseState } from "./status-flow";

// ===== Checkout de pedidos (RF-VEN-07): separação e conferência =====

export const CHECKOUT_MODES = ["by_order", "single_product"] as const;
export type CheckoutMode = (typeof CHECKOUT_MODES)[number];
export const CHECKOUT_SORTS = ["oldest", "newest"] as const;
export type CheckoutSort = (typeof CHECKOUT_SORTS)[number];

/** Código bipado (GTIN, SKU, número do pedido...): o leitor manda no máximo isso. */
export const MAX_SCAN_CODE_LENGTH = 60;
/** Sessões abertas por empresa (as órfãs são canceladas antes de contar). */
export const MAX_OPEN_CHECKOUT_SESSIONS = 500;
/** Leituras (bipagens e reinícios) por sessão. */
export const MAX_CHECKOUT_READS = 10_000;
/** Pedidos por relatório de separação. */
export const MAX_PICKING_ORDERS = 200;
/** Últimas leituras devolvidas com a sessão. */
export const CHECKOUT_RECENT_READS = 50;
/** Linhas (pedido × produto, kits expandidos) somadas no relatório de separação; acima = `limit_reached`. */
export const MAX_PICKING_ROWS = 20_000;
/**
 * WebSocket: conexões por empresa e por usuário, e vida de cada uma. Curta (5 min): depois reconecta e passa pela
 * autenticação de novo, então logout, troca de papel ou perda de acesso valem em minutos.
 */
export const CHECKOUT_SOCKETS_PER_COMPANY = 100;
export const CHECKOUT_SOCKETS_PER_USER = 5;
export const CHECKOUT_SOCKET_TTL_MS = 5 * 60 * 1000;

/**
 * Vencimento do WebSocket: o menor entre agora + TTL e o fim da sessão de login (ms). Sem sessão informada, só o TTL;
 * valor inválido (NaN) vence na hora.
 */
export function checkoutSocketExp(now: number, sessionExpiresAt: number | null): number {
  const ttl = now + CHECKOUT_SOCKET_TTL_MS;
  if (sessionExpiresAt === null) return ttl;
  if (!Number.isFinite(sessionExpiresAt)) return now;
  return Math.min(ttl, sessionExpiresAt);
}

/** Tira espaços e caracteres de controle que o leitor manda (Enter, Tab). Vazio ou longo demais = `invalid_code`. */
export function normalizeScanCode(raw: string): string {
  // eslint-disable-next-line no-control-regex
  const code = raw.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  if (!code || code.length > MAX_SCAN_CODE_LENGTH)
    throw new DomainError("invalid_code", `Código inválido: informe até ${MAX_SCAN_CODE_LENGTH} caracteres.`);
  return code;
}

/** Chave de acesso da NF-e/NFC-e: 44 dígitos com DV módulo 11 (pesos 2 a 9 da direita; resto 0 ou 1 = 0). */
export function isAccessKey(code: string): boolean {
  if (!/^\d{44}$/.test(code)) return false;
  let sum = 0;
  let w = 2;
  for (let i = 42; i >= 0; i--) {
    sum += Number(code[i]) * w;
    w = w === 9 ? 2 : w + 1;
  }
  const r = sum % 11;
  return Number(code[43]) === (r < 2 ? 0 : 11 - r);
}

export type OrderCode =
  { kind: "number"; number: number } | { kind: "access_key" } | { kind: "text"; value: string };

/** O que foi bipado na fila: número do pedido (até 9 dígitos), chave de acesso ou texto (nº na loja, pedido do cliente). */
export function classifyOrderCode(code: string): OrderCode {
  if (/^\d{1,9}$/.test(code) && Number(code) > 0) return { kind: "number", number: Number(code) };
  if (isAccessKey(code)) return { kind: "access_key" };
  return { kind: "text", value: code };
}

/** Produto casa com o código por GTIN, GTIN tributário ou SKU (este sem diferenciar maiúsculas). */
export function productMatchesCode(
  p: { gtin: string | null; gtinTax: string | null; sku: string | null },
  code: string,
): boolean {
  return (
    p.gtin === code || p.gtinTax === code || (p.sku !== null && p.sku.toUpperCase() === code.toUpperCase())
  );
}

export type PickLine = { id: string; productId: string; requiredQ4: number; checkedQ4: number };

/**
 * Distribui a leitura entre as linhas dos produtos casados: enche a primeira com saldo a conferir e transborda para as
 * seguintes, na ordem. Produto fora do pedido = `item_not_in_order`; mais do que falta conferir = `over_read` (nada
 * muda). Devolve só as linhas alteradas com o novo conferido.
 */
export function allocateRead(
  lines: readonly PickLine[],
  productIds: Iterable<string>,
  qtyQ4: number,
): { id: string; checkedQ4: number }[] {
  if (!Number.isSafeInteger(qtyQ4) || qtyQ4 < 1 || qtyQ4 > MAX_STOCK_QTY_Q4)
    throw new DomainError("invalid_qty", "Quantidade lida inválida.");
  const ids = new Set(productIds);
  const candidates = lines.filter((l) => ids.has(l.productId));
  if (candidates.length === 0)
    throw new DomainError("item_not_in_order", "Este produto não faz parte do pedido.");
  const room = candidates.reduce((s, l) => s + (l.requiredQ4 - l.checkedQ4), 0);
  if (qtyQ4 > room)
    throw new DomainError(
      "over_read",
      room === 0
        ? "Este produto já foi todo conferido."
        : "Quantidade acima do que falta conferir deste produto no pedido.",
    );
  const out: { id: string; checkedQ4: number }[] = [];
  let left = qtyQ4;
  for (const l of candidates) {
    if (left === 0) break;
    const take = Math.min(left, l.requiredQ4 - l.checkedQ4);
    if (take === 0) continue;
    out.push({ id: l.id, checkedQ4: l.checkedQ4 + take });
    left -= take;
  }
  return out;
}

export type CheckoutProgress = {
  requiredQ4: number;
  checkedQ4: number;
  linesTotal: number;
  linesDone: number;
  complete: boolean;
};

/**
 * Progresso da conferência. Somas em BigInt; total fora do inteiro seguro = `invalid_qty` (não devolve número
 * impreciso). Na prática não acontece: a abertura recusa linha acima de MAX_STOCK_QTY_Q4 (10^12) e o teto é de
 * MAX_DOC_STOCK_LINES (2.000) linhas, então o total fica abaixo de 2 × 10^15 < 2^53.
 */
export function checkoutProgress(
  lines: readonly { requiredQ4: number; checkedQ4: number }[],
): CheckoutProgress {
  let req = 0n;
  let chk = 0n;
  let done = 0;
  for (const l of lines) {
    req += BigInt(l.requiredQ4);
    chk += BigInt(l.checkedQ4);
    if (l.checkedQ4 >= l.requiredQ4) done++;
  }
  const safe = (v: bigint) => {
    const n = Number(v);
    if (!Number.isSafeInteger(n))
      throw new DomainError("invalid_qty", "Quantidade da conferência acima do limite.");
    return n;
  };
  return {
    requiredQ4: safe(req),
    checkedQ4: safe(chk),
    linesTotal: lines.length,
    linesDone: done,
    complete: lines.length > 0 && done === lines.length,
  };
}

/** Desfecho do "Salvar": completo conclui; incompleto só como parcial confirmado; nada conferido recusa. */
export function finishOutcome(p: CheckoutProgress, partial: boolean): "complete" | "partial" {
  if (p.complete) return "complete";
  if (p.checkedQ4 === 0)
    throw new DomainError(
      "nothing_checked",
      "Nenhum item foi conferido ainda: bipe os itens antes de salvar.",
    );
  if (!partial)
    throw new DomainError(
      "checkout_incomplete",
      "Ainda há itens a conferir. Confirme para salvar como checkout parcial.",
    );
  return "partial";
}

export type FingerprintItem = {
  id: string;
  productId: string | null;
  qtyQ4: number;
  warehouseId: string | null;
  /** Componentes do kit (o que vira linha a conferir): mudar a composição muda a impressão digital. */
  kit?: readonly { componentId: string; qtyQ4: number }[];
};

/**
 * Impressão digital dos itens do pedido (item, produto, quantidade, depósito e, no kit, os componentes): FNV-1a 32
 * bits + quantidade de itens. Detecta pedido alterado depois de aberta a conferência (não é segurança, é detecção de
 * mudança). Item sem kit gera a mesma entrada de antes.
 */
export function itemsFingerprint(items: readonly FingerprintItem[]): string {
  const s = JSON.stringify(
    items.map((i) => {
      const base: unknown[] = [i.id, i.productId, i.qtyQ4, i.warehouseId];
      if (i.kit) base.push(i.kit.map((c) => [c.componentId, c.qtyQ4]));
      return base;
    }),
  );
  let h = 0x811c9dc5;
  for (let k = 0; k < s.length; k++) {
    h ^= s.charCodeAt(k);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${h.toString(16).padStart(8, "0")}:${items.length}`;
}

export type PickingRow = {
  orderNumber: number;
  productId: string;
  name: string;
  sku: string | null;
  gtin: string | null;
  location: string | null;
  unit: string;
  qtyQ4: number;
};
export type PickingLine = Omit<PickingRow, "orderNumber"> & { orderNumbers: number[] };

/**
 * Relatório de separação (picking list consolidado): soma por produto (BigInt; acima do limite = `invalid_qty`), junta
 * os números dos pedidos e ordena pela localização (sem localização no fim), depois nome e id.
 */
export function pickingList(rows: readonly PickingRow[]): PickingLine[] {
  const acc = new Map<string, { line: Omit<PickingLine, "qtyQ4">; qty: bigint; orders: Set<number> }>();
  for (const r of rows) {
    let a = acc.get(r.productId);
    if (!a) {
      const line = {
        productId: r.productId,
        name: r.name,
        sku: r.sku,
        gtin: r.gtin,
        location: r.location,
        unit: r.unit,
        orderNumbers: [],
      };
      a = { line, qty: 0n, orders: new Set() };
      acc.set(r.productId, a);
    }
    a.qty += BigInt(r.qtyQ4);
    a.orders.add(r.orderNumber);
  }
  const out: PickingLine[] = [];
  for (const a of acc.values()) {
    const qtyQ4 = Number(a.qty);
    if (!Number.isSafeInteger(qtyQ4))
      throw new DomainError("invalid_qty", "Quantidade do relatório acima do limite.");
    out.push({ ...a.line, qtyQ4, orderNumbers: [...a.orders].sort((x, y) => x - y) });
  }
  const cmp = (x: string, y: string) => (x < y ? -1 : x > y ? 1 : 0);
  return out.sort((x, y) => {
    if (x.location !== y.location) {
      if (x.location === null) return 1;
      if (y.location === null) return -1;
      return cmp(x.location, y.location);
    }
    return cmp(x.name, y.name) || cmp(x.productId, y.productId);
  });
}

export type CheckoutSettingsValue = {
  sourceStatusIds: readonly string[];
  doneStatusId: string;
  partialStatusId: string | null;
};

/**
 * Configuração do checkout contra as situações do módulo de pedidos: origem (fila), concluído e parcial todos em
 * andamento (o checkout nunca abre, conclui nem cancela o pedido: sair de "aberto" reservaria estoque), concluído fora
 * da origem e diferente do parcial. Devolve o primeiro problema (campo e mensagem) ou null.
 */
export function checkoutSettingsProblem(
  s: CheckoutSettingsValue,
  statuses: ReadonlyMap<string, { baseState: BaseState }>,
): { field: keyof CheckoutSettingsValue; message: string } | null {
  if (s.sourceStatusIds.length === 0 || new Set(s.sourceStatusIds).size !== s.sourceStatusIds.length)
    return { field: "sourceStatusIds", message: "Escolha ao menos uma situação, sem repetir." };
  for (const id of s.sourceStatusIds) {
    if (statuses.get(id)?.baseState !== "in_progress")
      return {
        field: "sourceStatusIds",
        message: "A fila do checkout só aceita situações em andamento.",
      };
  }
  if (statuses.get(s.doneStatusId)?.baseState !== "in_progress")
    return { field: "doneStatusId", message: "Escolha uma situação em andamento (ex.: Verificado)." };
  if (s.sourceStatusIds.includes(s.doneStatusId))
    return { field: "doneStatusId", message: "A situação ao concluir não pode estar na fila do checkout." };
  if (s.partialStatusId !== null) {
    if (statuses.get(s.partialStatusId)?.baseState !== "in_progress")
      return {
        field: "partialStatusId",
        message: "Escolha uma situação em andamento (ex.: Checkout parcial).",
      };
    if (s.partialStatusId === s.doneStatusId)
      return { field: "partialStatusId", message: "A situação parcial deve ser diferente da de concluído." };
  }
  return null;
}
